import { createContext, useEffect, useState, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface Workspace {
    id: string;
    name: string;
    domain: string | null;
    is_personal: boolean;
    role: 'admin' | 'member' | 'viewer';
}

interface WorkspaceContextType {
    workspaces: Workspace[];
    currentWorkspace: Workspace | null;
    loading: boolean; // Workspace loading state
    isSuperAdmin: boolean;
    switchWorkspace: (workspaceId: string) => Promise<void>;
    updateWorkspaceName: (name: string) => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    // Track the latest fetch controller to handle race conditions
    const fetchControllerRef = useRef<AbortController | null>(null);

    const fetchWorkspaces = async (userId: string) => {
        // Abort any pending fetch
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
        }

        const controller = new AbortController();
        fetchControllerRef.current = controller;

        try {
            // Fetch workspace members and details
            const membersPromise = supabase
                .from('workspace_members')
                .select(`
                    role,
                    workspaces (
                        id,
                        name,
                        domain,
                        is_personal
                    )
                `)
                .eq('user_id', userId)
                .abortSignal(controller.signal as AbortSignal);

            // Fetch profile for default workspace and super admin status
            const profilePromise = supabase
                .from('profiles')
                .select('default_workspace_id, is_super_admin')
                .eq('user_id', userId)
                .maybeSingle()
                .abortSignal(controller.signal as AbortSignal);

            const [membersResult, profileResult] = await Promise.all([membersPromise, profilePromise]);

            if (membersResult.error) throw membersResult.error;
            if (profileResult.error && profileResult.error.code !== 'PGRST116') throw profileResult.error;

            const members = membersResult.data;
            const profile = profileResult.data;

            if (members) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const formattedWorkspaces: Workspace[] = members.map((m: any) => ({
                    id: m.workspaces!.id,
                    name: m.workspaces!.name,
                    domain: m.workspaces!.domain,
                    is_personal: m.workspaces!.is_personal,
                    role: m.role
                }));

                if (controller.signal.aborted) return;
                setWorkspaces(formattedWorkspaces);

                // Set super admin status
                const isSuperAdminUser = user?.email === 'haynesjasper@gmail.com' && profile?.is_super_admin === true;
                setIsSuperAdmin(isSuperAdminUser);

                // Determine current workspace
                let selectedWorkspace: Workspace | undefined;

                if (profile?.default_workspace_id) {
                    selectedWorkspace = formattedWorkspaces.find(w => w.id === profile.default_workspace_id);
                }

                if (!selectedWorkspace && formattedWorkspaces.length > 0) {
                    selectedWorkspace = formattedWorkspaces[0];
                }

                if (selectedWorkspace) {
                    setCurrentWorkspace(selectedWorkspace);
                }
            }
        } catch (error: unknown) {
            const err = error as { name?: string, message?: string };
            if (err.name === 'AbortError' || err.message?.includes('aborted')) {
                return;
            }
            console.error('Error fetching workspaces:', error);
        } finally {
            if (fetchControllerRef.current === controller) {
                fetchControllerRef.current = null;
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (user) {
            setLoading(true);
            fetchWorkspaces(user.id);
        } else {
            setWorkspaces([]);
            setCurrentWorkspace(null);
            setIsSuperAdmin(false);
            setLoading(false);
        }

        return () => {
            if (fetchControllerRef.current) {
                fetchControllerRef.current.abort();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]); // Depend on user ID

    const switchWorkspace = async (workspaceId: string) => {
        const workspace = workspaces.find(w => w.id === workspaceId);
        if (workspace) {
            setCurrentWorkspace(workspace);
            // Optionally update profile default_workspace_id
            if (user) {
                await supabase
                    .from('profiles')
                    .update({ default_workspace_id: workspaceId })
                    .eq('user_id', user.id);
            }
        }
    };

    const updateWorkspaceName = async (name: string) => {
        if (!currentWorkspace) return;

        try {
            const { error } = await supabase
                .from('workspaces')
                .update({ name })
                .eq('id', currentWorkspace.id);

            if (error) throw error;

            // Update local state
            const updatedWorkspace = { ...currentWorkspace, name };
            setCurrentWorkspace(updatedWorkspace);
            setWorkspaces(prev => prev.map(w => w.id === currentWorkspace.id ? updatedWorkspace : w));
        } catch (error) {
            console.error('Error updating workspace name:', error);
            throw error;
        }
    };

    return (
        <WorkspaceContext.Provider value={{
            workspaces,
            currentWorkspace,
            loading,
            isSuperAdmin,
            switchWorkspace,
            updateWorkspaceName
        }}>
            {children}
        </WorkspaceContext.Provider>
    );
}
